import { useEffect, useRef, useCallback } from 'react';
import { authApi } from '@/services/api';

interface UseSessionRefreshOptions {
  /**
   * Interval in milliseconds to check for token refresh (default: 5 minutes)
   */
  refreshInterval?: number;
  /**
   * Time in milliseconds to consider user as inactive (default: 2 minutes)
   */
  inactivityThreshold?: number;
  /**
   * Enable/disable the session refresh (default: true)
   */
  enabled?: boolean;
}

/**
 * Hook to automatically refresh user session tokens based on user activity.
 *
 * This hook:
 * - Tracks user activity (mouse, keyboard, touch events)
 * - Periodically refreshes tokens when user is active
 * - Prevents unnecessary API calls when user is idle
 * - Uses the backend's check_login endpoint which validates and refreshes tokens
 *
 * The backend proactively refreshes tokens within 5 minutes of expiration,
 * so calling check_login every 5 minutes ensures tokens never expire during active use.
 */
export const useSessionRefresh = (options: UseSessionRefreshOptions = {}) => {
  const {
    refreshInterval = 5 * 60 * 1000, // 5 minutes
    inactivityThreshold = 2 * 60 * 1000, // 2 minutes
    enabled = true,
  } = options;

  const lastActivityRef = useRef<number>(Date.now());
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef<boolean>(false);

  /**
   * Update the last activity timestamp
   */
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  /**
   * Check if user is currently active based on last activity time
   */
  const isUserActive = useCallback((): boolean => {
    // inactivityThreshold of 0 means "never timeout" — always active
    if (inactivityThreshold === 0) return true;
    const timeSinceLastActivity = Date.now() - lastActivityRef.current;
    return timeSinceLastActivity < inactivityThreshold;
  }, [inactivityThreshold]);

  /**
   * Refresh the session by calling check_login endpoint
   */
  const refreshSession = useCallback(async () => {
    // Skip if already refreshing
    if (isRefreshingRef.current) {
      return;
    }

    // Skip if user is not active
    if (!isUserActive()) {
      return;
    }

    // Skip if no token exists
    const idToken = localStorage.getItem('id_token');
    if (!idToken) {
      console.log('[SessionRefresh] Skipping refresh - no token found');
      return;
    }

    try {
      isRefreshingRef.current = true;
      console.log('[SessionRefresh] Refreshing session tokens...');

      const response = await authApi.checkLogin();

      if (response && response.id_token) {
        console.log('[SessionRefresh] Session tokens refreshed successfully');
      } else {
        console.warn('[SessionRefresh] Token refresh returned no data');
      }
    } catch (error) {
      console.error('[SessionRefresh] Failed to refresh session:', error);
      // Don't throw - let the global error handler deal with 403s
    } finally {
      isRefreshingRef.current = false;
    }
  }, [isUserActive]);

  /**
   * Set up activity listeners
   */
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Activity event types to track
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    // Throttle activity updates to avoid excessive updates
    let throttleTimeout: NodeJS.Timeout | null = null;
    const throttledUpdateActivity = () => {
      if (throttleTimeout) {
        return;
      }
      throttleTimeout = setTimeout(() => {
        updateActivity();
        throttleTimeout = null;
      }, 1000); // Update at most once per second
    };

    // Add event listeners
    activityEvents.forEach((event) => {
      window.addEventListener(event, throttledUpdateActivity, { passive: true });
    });

    // Initial activity timestamp
    updateActivity();

    // Cleanup
    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, throttledUpdateActivity);
      });
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [enabled, updateActivity]);

  /**
   * Set up periodic refresh interval
   */
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Perform initial refresh after a short delay
    const initialRefreshTimeout = setTimeout(() => {
      refreshSession();
    }, 10000); // 10 seconds after mount

    // Set up periodic refresh
    refreshIntervalRef.current = setInterval(() => {
      refreshSession();
    }, refreshInterval);

    // Cleanup
    return () => {
      clearTimeout(initialRefreshTimeout);
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [enabled, refreshInterval, refreshSession]);

  return {
    /**
     * Manually trigger a session refresh
     */
    refresh: refreshSession,
    /**
     * Check if user is currently active
     */
    isActive: isUserActive,
  };
};
